/**
 * "Decision needed" view — v11.7 V117-1-10 (ADR-0111 §7, D-116-14; closed design: ux-adversary run
 * `v117-decisions`, round 3 + patch). Route: /w/:slug/app/programs/ai-regulatory/scope?view=decisions
 * [&rule=<rule_id>][&from=<app path>[&from_label=<name>]], a view of the AI Regulatory Scope tab (no nav entry).
 *
 * A contested law (stayed, or at high preemption risk) counts toward nothing until an owner or admin records whether
 * it applies. This view lists those laws (one card per law; the API is per rule), opens one to decide or change it,
 * and shows a member who decides. It states facts and records the choice; it never advises which choice is right.
 *
 * Reads GET /rules/applicability-decisions (any member). Writes POST /rules/:ruleId/applicability (owner / admin,
 * server-enforced) with `expected_chosen_at` = the decision the reader saw, so a decision someone else saved meanwhile
 * comes back as a 409 conflict (Keep theirs / Replace with mine) instead of being overwritten silently.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, EmptyState, Field, Spinner } from '@aigovops/design-system';
import { ApiError } from '../../lib/api';
import { invalidationMap } from '../../lib/query-keys';
import {
  CHOICE_WORD, chooseRuleApplicability, decisionKeys, decisionsHref, effWord, firstName, fmtLawDate, getRuleDecisions, groupByLaw,
  isFuture, daysUntil, lawOfRule, reviewTarget, safeFrom, splitLaws, todaySub, todayTitle, whenText,
  type ApplicabilityChoice, type DecisionRecord, type LawDecision, type RuleDecider, type RuleDecisionsView,
} from '../../lib/contested-decisions';
import '../../pages/today.css';
import './contested-decisions.css';

/** Both conflict buttons ignore presses this long after the warning appears (no accidental overwrite). */
export const CONFLICT_LOCK_MS = 600;

type Draft = { choice?: ApplicabilityChoice; reason: string };
type Conflict = { key: string; rec: DecisionRecord };
type CopyState = { key: string; msg: string; fallback?: { kind: 'link' | 'reason'; text: string } };

const todayIso = () => new Date().toISOString().slice(0, 10);

export function useRuleDecisions(slug: string, enabled = true) {
  return useQuery({ queryKey: decisionKeys.all(slug), queryFn: () => getRuleDecisions(slug), enabled, staleTime: 30_000, retry: false });
}

// ---- Small pieces ------------------------------------------------------------------------------------------------

const Glyph = {
  pause: <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><rect x="3" y="2.5" width="2.6" height="9" rx=".8" fill="currentColor" /><rect x="8.4" y="2.5" width="2.6" height="9" rx=".8" fill="currentColor" /></svg>,
  risk: <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M7 1.8L12.6 11.6H1.4z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /><path d="M7 5.6v2.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /><circle cx="7" cy="9.9" r=".8" fill="currentColor" /></svg>,
  check: <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M3 7.4l2.6 2.6L11 4.4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  dash: <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M3.5 7h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>,
  warn: <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><circle cx="7" cy="7" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M7 4v3.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /><circle cx="7" cy="10" r=".9" fill="currentColor" /></svg>,
  chev: <svg className="cd-chev" width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M4.5 2.5L8 6l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  back: <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M7.5 2.5L4 6l3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
};

function Chip({ law }: { law: LawDecision }) {
  if (!law.current) return <span className="cd-chip" data-c="needed"><span className="cd-dot" aria-hidden="true" />Decision needed</span>;
  return law.current.choice === 'applies'
    ? <span className="cd-chip" data-c="applies">{Glyph.check}Applies</span>
    : <span className="cd-chip" data-c="not_counted">{Glyph.dash}Does not apply · Not counted</span>;
}

/** The one decision record (members and deciders alike): "<Name> · <date>" and the quoted reason. */
export function DecisionRecordBox({ who, reason, id }: { who: ReactNode; reason: string; id?: string }) {
  return (
    <div className="cd-record" data-part="record">
      <p className="cd-record-who">{who}</p>
      <p className="cd-record-reason" id={id}>{reason}</p>
    </div>
  );
}

const recordWho = (r: DecisionRecord) => `${r.byName} · ${fmtLawDate(r.at)}`;

function History({ law }: { law: LawDecision }) {
  if (law.history.length < 2) return null;
  return (
    <details className="cd-disc" data-part="history">
      <summary>{Glyph.chev}{`History · ${law.history.length} decisions`}<span className="sr-only">{` for ${law.name}`}</span></summary>
      <div className="cd-disc-body">
        <ol className="cd-history">
          {law.history.map((h, i) => (
            <li key={`${h.at}-${i}`} data-current={i === 0 && law.current ? '' : undefined}>
              <span className="cd-h-what">{CHOICE_WORD[h.choice]}{i === 0 && law.current ? ' · current' : ''}</span>
              <span className="cd-h-who">{recordWho(h)}</span>
              <span className="cd-h-why">{h.reason}</span>
            </li>
          ))}
        </ol>
      </div>
    </details>
  );
}

function Source({ law }: { law: LawDecision }) {
  if (!law.note) return null;
  return (
    <details className="cd-disc" data-part="source">
      <summary>{Glyph.chev}Source<span className="sr-only">{` for ${law.name}`}</span></summary>
      <div className="cd-disc-body"><p>{law.note}</p></div>
    </details>
  );
}

const orList = (names: string[]) => (names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`);

/** Who to ask: the most recent decider first (if they can still decide), else the owner; never the viewer. */
function askOrder(deciders: readonly RuleDecider[], viewerId: string, law: LawDecision): RuleDecider[] {
  const ds = deciders.filter((p) => p.user_id !== viewerId);
  const lead = law.current ? ds.find((p) => p.user_id === law.current!.by) : undefined;
  const first = lead ?? ds.find((p) => p.role === 'owner') ?? ds[0];
  return first ? [first, ...ds.filter((p) => p !== first)] : ds;
}

// ---- The view ----------------------------------------------------------------------------------------------------

export function ContestedDecisions({ slug, viewerId, overviewHref }: { slug: string; viewerId: string; overviewHref: string }) {
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const q = useRuleDecisions(slug);
  const ruleParam = params.get('rule');
  const from = safeFrom(slug, params.get('from'));
  const fromName = (params.get('from_label') ?? '').trim().slice(0, 120) || 'the control';
  const today = todayIso();

  const laws = useMemo(() => groupByLaw(q.data?.decisions ?? []), [q.data]);
  const { waiting, decided } = useMemo(() => splitLaws(laws), [laws]);
  const deepLaw = lawOfRule(laws, ruleParam);
  const stale = !!q.data && !!ruleParam && !deepLaw;

  const [openKey, setOpenKey] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [errs, setErrs] = useState<Record<string, { choice?: boolean; reason?: string }>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [failed, setFailed] = useState<{ key: string; message: string } | null>(null);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [locked, setLocked] = useState(false);
  const [lostRole, setLostRole] = useState(false);
  const [lost, setLost] = useState<{ key: string; draft: Required<Draft> } | null>(null);
  const [kept, setKept] = useState<{ key: string; draft: Draft } | null>(null);
  const [flash, setFlash] = useState<{ key: string; text: string } | null>(null);
  const [copied, setCopied] = useState<CopyState | null>(null);
  /** The decision each rule had when the reader opened the law (what they saw): sent as expected_chosen_at. */
  const seen = useRef<Map<string, Map<string, string | null>>>(new Map());
  /** Rules of a law already written in the current attempt (a retry skips them). */
  const written = useRef<Map<string, Set<string>>>(new Map());
  const liveRef = useRef<HTMLDivElement>(null);
  const focusTarget = useRef<{ id: string; scroll?: ScrollLogicalPosition; select?: boolean } | null>(null);
  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const deepDone = useRef(false);
  const [, bump] = useState(0);

  const canDecide = !!q.data?.can_decide && !lostRole;
  const deciders = q.data?.deciders ?? [];

  const later = (fn: () => void, ms: number) => { timers.current.push(setTimeout(fn, ms)); };
  useEffect(() => () => { timers.current.forEach(clearTimeout); }, []);
  const focusLater = (id: string, scroll?: ScrollLogicalPosition, select?: boolean) => { focusTarget.current = { id, scroll, select }; bump((n) => n + 1); };
  useEffect(() => {
    const t = focusTarget.current;
    if (!t) return;
    const el = document.getElementById(t.id);
    if (!el) return;
    focusTarget.current = null;
    if (t.scroll) el.scrollIntoView?.({ block: t.scroll });
    el.focus({ preventScroll: true });
    if (t.select && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) el.select();
  });
  const announce = (m: string) => { const l = liveRef.current; if (!l) return; l.textContent = ''; later(() => { l.textContent = m; }, 30); };
  const clearLive = () => { if (liveRef.current) liveRef.current.textContent = ''; };

  const snapshot = (law: LawDecision) => seen.current.set(law.key, new Map(law.rules.map((r) => [r.rule_id, r.choice?.chosen_at ?? null])));

  // Deep link: open the named law once the data is in (scroll + focus its heading); a stale link lands on its notice.
  useEffect(() => {
    if (!q.data || deepDone.current || !ruleParam) return;
    deepDone.current = true;
    if (deepLaw) { snapshot(deepLaw); setOpenKey(deepLaw.key); focusLater(`law-name-${deepLaw.key}`, 'start'); }
    else focusLater('cd-stale-title');
  }, [q.data, ruleParam]); // eslint-disable-line react-hooks/exhaustive-deps

  const openLaw = openKey ? laws.find((l) => l.key === openKey) ?? null : null;
  useEffect(() => {
    const before = document.title;
    document.title = `${openLaw ? `${openLaw.name} · ` : ''}Decisions · Scope · AI Regulatory · GAC Pro`;
    return () => { document.title = before; };
  }, [openLaw?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: decisionKeys.all(slug) }),
      qc.invalidateQueries({ queryKey: ['ai-grid', slug] }),
      qc.invalidateQueries({ queryKey: ['ai-sources', slug] }),
      ...invalidationMap['program:changed'](slug).map((queryKey) => qc.invalidateQueries({ queryKey })),
    ]);
  };

  function open(law: LawDecision) {
    setFlash(null); setCopied(null);
    if (kept?.key === law.key) { setDrafts((d) => ({ ...d, [law.key]: kept.draft })); setKept(null); }
    snapshot(law);
    setOpenKey(law.key);
    focusLater(`law-name-${law.key}`, 'start');
  }
  function close(law: LawDecision) {
    setOpenKey(null); setConflict(null); setFailed(null); setErrs((e) => ({ ...e, [law.key]: {} }));
    setDrafts(({ [law.key]: _, ...rest }) => rest);
    written.current.delete(law.key);
    focusLater(`law-open-${law.key}`);
  }
  const setDraft = (key: string, patch: Partial<Draft>) => {
    setDrafts((d) => ({ ...d, [key]: { ...(d[key] ?? { reason: '' }), ...patch } }));
    setErrs((e) => {
      const cur = e[key] ?? {};
      if (patch.choice && cur.choice) return { ...e, [key]: { ...cur, choice: false } };
      if (patch.reason !== undefined && cur.reason && patch.reason.trim().length >= 3) return { ...e, [key]: { ...cur, reason: undefined } };
      return e;
    });
  };

  function validate(law: LawDecision): Required<Draft> | null {
    const d = drafts[law.key] ?? { reason: '' };
    const reason = d.reason.trim();
    const e = { choice: !d.choice, reason: reason.length === 0 ? 'Add a reason so your auditor can see why.' : reason.length < 3 ? 'Write at least 3 characters.' : undefined };
    setErrs((x) => ({ ...x, [law.key]: e }));
    if (e.choice) { focusLater(`choice-${law.key}-applies`); return null; }
    if (e.reason) { focusLater(`reason-${law.key}`); return null; }
    return { choice: d.choice!, reason };
  }

  async function save(law: LawDecision) {
    if (saving) { if (saving === law.key) announce('Saving…'); return; }
    const v = validate(law);
    if (!v) return;
    setSaving(law.key); setFailed(null); announce('Saving…');
    const done = written.current.get(law.key) ?? new Set<string>();
    written.current.set(law.key, done);
    const expected = seen.current.get(law.key) ?? new Map<string, string | null>();
    let at: string | null = null;
    try {
      for (const r of law.rules) {
        if (done.has(r.rule_id)) continue;
        at = r.rule_id;
        const exp = expected.has(r.rule_id) ? expected.get(r.rule_id)! : r.choice?.chosen_at ?? null;
        await chooseRuleApplicability(slug, r.rule_id, { choice: v.choice, reason: v.reason, expected_chosen_at: exp });
        done.add(r.rule_id);
      }
    } catch (e) {
      setSaving(null);
      const body = e instanceof ApiError ? (e.body as { code?: string; current?: { choice: ApplicabilityChoice; reason: string; chosen_by: string; chosen_by_name?: string; chosen_at: string } | null } | undefined) : undefined;
      if (e instanceof ApiError && e.status === 409 && body?.code === 'conflict' && body.current) {
        await showConflict(law, at!, { choice: body.current.choice, reason: body.current.reason, by: body.current.chosen_by, byName: body.current.chosen_by_name || 'Someone', at: body.current.chosen_at });
        return;
      }
      if (e instanceof ApiError && e.status === 403) {
        setLostRole(true); setConflict(null); setLost({ key: law.key, draft: v }); written.current.delete(law.key);
        focusLater(`lost-title-${law.key}`, 'start');
        return;
      }
      // Network or server: the draft stays; Try again sits where Save was (same element, keeps focus).
      clearLive();
      setFailed({ key: law.key, message: e instanceof ApiError && e.status < 500 && e.status !== 409 ? e.message : `We couldn't reach GAC Pro. Your choice and reason are still here.` });
      focusLater(`save-${law.key}`, 'nearest');
      return;
    }
    written.current.delete(law.key);
    seen.current.delete(law.key);
    setSaving(null); setConflict(null); setFailed(null); setKept(null); setOpenKey(null);
    setDrafts(({ [law.key]: _, ...rest }) => rest);
    setFlash({ key: law.key, text: 'Saved.' });
    await refresh();
    announce(`Saved. ${law.name}: ${CHOICE_WORD[v.choice]}.`);
    // Next: the next law waiting on a decision, else the saved law's heading.
    const fresh = qc.getQueryData<RuleDecisionsView>(decisionKeys.all(slug));
    const next = fresh ? splitLaws(groupByLaw(fresh.decisions)).waiting.find((l) => l.key !== law.key) : undefined;
    if (next) focusLater(`law-open-${next.key}`, 'nearest'); else focusLater(`law-name-${law.key}`, 'start');
  }

  /** The server's record replaced ours; re-read so "what I saw" is now theirs, keep the draft, focus the warning. */
  async function showConflict(law: LawDecision, ruleId: string, rec: DecisionRecord) {
    let fresh: RuleDecisionsView | undefined;
    try { fresh = await qc.fetchQuery({ queryKey: decisionKeys.all(slug), queryFn: () => getRuleDecisions(slug), staleTime: 0 }); } catch { fresh = undefined; }
    const freshLaw = fresh ? groupByLaw(fresh.decisions).find((l) => l.key === law.key) : undefined;
    if (freshLaw) snapshot(freshLaw);
    else seen.current.get(law.key)?.set(ruleId, rec.at);
    clearLive();
    setConflict({ key: law.key, rec }); setLocked(true); setOpenKey(law.key);
    later(() => setLocked(false), CONFLICT_LOCK_MS);
    focusLater(`conflict-title-${law.key}`, 'nearest');
  }

  function keepTheirs(law: LawDecision) {
    if (locked || !conflict) return;
    const d = drafts[law.key];
    setKept(d && d.reason.trim() ? { key: law.key, draft: d } : null);
    setConflict(null); setFailed(null); setOpenKey(null); setCopied(null);
    setDrafts(({ [law.key]: _, ...rest }) => rest);
    written.current.delete(law.key);
    const text = `Kept ${conflict.rec.byName}'s decision.`;
    setFlash({ key: law.key, text });
    announce(text);
    focusLater(`law-name-${law.key}`, 'start');
  }

  async function copy(key: string, text: string, kind: 'link' | 'reason') {
    setCopied({ key, msg: '' });
    try {
      await navigator.clipboard.writeText(text);
      later(() => setCopied({ key, msg: kind === 'link' ? 'Link copied.' : 'Reason copied.' }), 30);
    } catch {
      setCopied({ key, msg: kind === 'link' ? "Couldn't copy. The link is selected below." : "Couldn't copy. Your reason is selected below.", fallback: { kind, text } });
      focusLater(`fb-${key}`, undefined, true);
    }
  }
  const linkTo = (law: LawDecision) => `${window.location.origin}${decisionsHref(slug, law.rules[0]!.rule_id)}`;

  function copyOut(k: string) {
    const c = copied?.key === k ? copied : null;
    return <>
      <p className="cd-copied" role="status" id={`copied-${k}`}>{c?.msg ?? ''}</p>
      {c?.fallback ? (
        <div className="cd-fallback">
          <label htmlFor={`fb-${k}`}>{c.fallback.kind === 'link' ? 'Link to this decision' : 'Your reason'}</label>
          {c.fallback.kind === 'link' ? <input id={`fb-${k}`} type="text" readOnly value={c.fallback.text} />
            : <textarea id={`fb-${k}`} readOnly rows={3} value={c.fallback.text} />}
        </div>
      ) : null}
    </>;
  }

  const backLink = (primary: boolean) => {
    const label = from ? `Back to ${fromName}` : 'Back to Laws and systems';
    const short = label.length > 40 ? `${label.slice(0, 39).trimEnd()}…` : label;
    return <Link className={`gp-linkbtn${primary ? ' btn-primary' : ''}`} to={from ?? overviewHref} aria-label={short !== label ? label : undefined}>{short}</Link>;
  };

  if (q.isLoading) return <Spinner label="Loading decisions" />;
  if (q.error || !q.data) {
    return <Alert variant="error"><p>Decisions could not load.</p><Button onClick={() => void q.refetch()}>Try again</Button></Alert>;
  }

  // ---- Cards ----
  function whoPanel(law: LawDecision) {
    const ds = askOrder(deciders, viewerId, law);
    const first = ds[0];
    const isLost = lost?.key === law.key;
    const subject = law.current ? `Revisit: ${law.name}` : `Decision needed: ${law.name}`;
    let body = law.current
      ? `Can you revisit whether ${law.name} applies? It is currently ${law.current.choice === 'applies' ? 'counted' : 'Not counted'}.`
      : `Can you decide whether ${law.name} applies to us? It is not counted until someone does.`;
    if (isLost) body = `I drafted a decision on ${law.name} but can no longer save it.\n\nMy draft: ${CHOICE_WORD[lost!.draft.choice]}\nReason: ${lost!.draft.reason}\n\nCan you decide?`;
    body += `\n\n${linkTo(law)}`;
    const mailto = (p: RuleDecider) => `mailto:${p.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    const verb = law.current ? 'to change it' : 'to decide';
    return (
      <div className="cd-who" role="group" aria-label={`Who decides on ${law.name}`} data-part="who-decides">
        {isLost ? <>
          <Alert variant="error" title="You can no longer decide" focusTitle={{ id: `lost-title-${law.key}` }} part="lost">
            <p>Your role changed, so nothing was saved.</p>
          </Alert>
          <DecisionRecordBox who={`Your draft · ${CHOICE_WORD[lost!.draft.choice]}`} reason={lost!.draft.reason} id={`draft-${law.key}`} />
        </> : law.current ? <>
          <h5 className="cd-who-h">{`${law.current.byName} decided this on ${fmtLawDate(law.current.at)}`}</h5>
          <DecisionRecordBox who={recordWho(law.current)} reason={law.current.reason} />
        </> : null}
        <p className="cd-who-label">{law.current ? 'Owner or admin can change it:' : 'Owner or admin decides:'}</p>
        {ds.length ? (
          <ul className="cd-deciders" aria-label={law.current ? 'Who can change it' : 'Who decides'}>
            {ds.map((p) => <li key={p.user_id}><b>{p.name}</b> <span className="cd-dim">{p.role}</span> <span className="cd-email">{p.email}</span></li>)}
          </ul>
        ) : <p className="cd-who-label">No other owner or admin in this workspace yet.</p>}
        <div className="cd-actions">
          {first ? ds.map((p, i) => (
            <a key={p.user_id} className={`gp-linkbtn${i === 0 ? ' btn-primary' : ''}`} href={mailto(p)}>{i === 0 ? `Ask ${firstName(p.name)} ${verb}` : `Ask ${firstName(p.name)}`}</a>
          )) : <Link className="gp-linkbtn btn-primary" to={`/w/${encodeURIComponent(slug)}/app/settings/team`}>Invite an owner or admin</Link>}
          {isLost ? <Button onClick={() => void copy(law.key, lost!.draft.reason, 'reason')}>Copy my reason</Button> : null}
          <Button onClick={() => void copy(law.key, linkTo(law), 'link')}>Copy link to this decision</Button>
        </div>
        {copyOut(law.key)}
      </div>
    );
  }

  function decideForm(law: LawDecision) {
    const key = law.key;
    const d = drafts[key] ?? { reason: '' };
    const e = errs[key] ?? {};
    const c = conflict?.key === key ? conflict.rec : null;
    const changing = !!law.current;
    const busy = saving === key;
    const err = failed?.key === key ? failed.message : null;
    const sub = c ? `Now: ${CHOICE_WORD[c.choice]} (${c.byName}).`
      : changing ? `Now: ${CHOICE_WORD[law.current!.choice]} (${law.current!.byName}). The earlier decision stays in History.` : 'You can change this later.';
    const onSubmit = (ev: FormEvent) => {
      ev.preventDefault();
      // In a conflict only a press on "Replace with mine" saves (no implicit submit, no Enter on a radio).
      if (conflict?.key === key) return;
      void save(law);
    };
    const opt = (v: ApplicabilityChoice, title: string, desc: string) => (
      <label className="cd-opt">
        <input type="radio" id={`choice-${key}-${v}`} name={`choice-${key}`} value={v} checked={d.choice === v} required
          aria-invalid={e.choice ? true : undefined} aria-describedby={e.choice ? `choice-sub-${key} choice-err-${key}` : `choice-sub-${key}`}
          onChange={() => setDraft(key, { choice: v })} />
        <span className="cd-opt-t"><b>{title}</b><span className="cd-opt-d">{desc}</span></span>
      </label>
    );
    const replaceLabel = (lead: string) => <>{lead}<span className="sr-only">{` (replaces ${c!.byName}'s decision)`}</span></>;
    return (
      <form className="cd-form" noValidate onSubmit={onSubmit} data-law={key} data-part="decide">
        <fieldset className="cd-choice" data-invalid={e.choice ? '' : undefined}>
          <legend>{changing || c ? 'Change the decision' : 'Does this law apply to you?'} <span className="cd-req">(required)</span></legend>
          <p className="cd-sub" id={`choice-sub-${key}`}>{sub}</p>
          <div className="cd-opts">
            {opt('applies', 'Applies', 'Its obligations count toward readiness and your pack.')}
            {opt('does_not_apply', 'Does not apply', 'Shown as Not counted. Its obligations stay out.')}
          </div>
          {e.choice ? <p className="cd-err" id={`choice-err-${key}`}>{Glyph.warn}<span>Choose Applies or Does not apply.</span></p> : null}
        </fieldset>
        <Field id={`reason-${key}`} label={changing || c ? 'Reason for the change' : 'Reason'} required errorRole="none" error={e.reason}
          hint={c ? `Saved as a change to ${firstName(c.byName)}'s decision. Edit your reason if needed.` : "Your auditor reads this. It is saved with your name and today's date."}>
          <textarea className="cd-reason" name="reason" value={d.reason} onChange={(ev) => setDraft(key, { reason: ev.currentTarget.value })}
            onKeyDown={(ev) => { if (ev.key === 'Enter' && (ev.ctrlKey || ev.metaKey)) { ev.preventDefault(); if (conflict?.key !== key) void save(law); } }} />
        </Field>
        {c || err ? (
          <div className="cd-alert-slot">
            {c ? (
              <Alert variant="warning" part="conflict" title={`${c.byName} decided this ${whenText(c.at)}: ${CHOICE_WORD[c.choice]}.`}
                focusTitle={{ id: `conflict-title-${key}`, describedBy: `conflict-body-${key}` }}>
                <p id={`conflict-body-${key}`}>{`Keep ${firstName(c.byName)}'s drops your draft. Replace with mine saves yours, and ${firstName(c.byName)}'s stays in History.`}</p>
              </Alert>
            ) : null}
            {err ? <Alert variant="error" part="save-error" title="Your decision wasn't saved"><p>{err}</p></Alert> : null}
          </div>
        ) : null}
        <div className="cd-actions">
          {c ? <>
            <Button variant="primary" type="button" data-keep="" aria-disabled={locked || busy ? true : undefined} onClick={() => keepTheirs(law)}>{`Keep ${firstName(c.byName)}'s`}</Button>
            <Button type="button" id={`save-${key}`} data-replace="" aria-disabled={locked || busy ? true : undefined}
              onClick={() => { if (!locked) void save(law); }}>
              {busy ? 'Saving…' : replaceLabel(err ? 'Try again: replace with mine' : 'Replace with mine')}
            </Button>
          </> : <>
            <Button variant="primary" type="submit" id={`save-${key}`} aria-disabled={busy ? true : undefined}>
              {busy ? 'Saving…' : err ? 'Try again' : changing ? 'Save new decision' : 'Save decision'}
            </Button>
            <Button variant="ghost" type="button" onClick={() => close(law)}>Cancel</Button>
          </>}
        </div>
      </form>
    );
  }

  function card(law: LawDecision, firstWaiting: LawDecision | undefined) {
    const isOpen = openKey === law.key;
    const ro = !canDecide;
    const names = askOrder(deciders, viewerId, law).map((p) => p.name);
    const f = flash?.key === law.key ? flash : null;
    const keptHere = kept?.key === law.key && !!f;
    let action: ReactNode = null;
    if (!isOpen) {
      action = !canDecide
        ? <Button id={`law-open-${law.key}`} onClick={() => open(law)}>{`See who decides on ${law.name}`}</Button>
        : law.current
          ? <Button id={`law-open-${law.key}`} onClick={() => open(law)}>Change<span className="sr-only">{` the decision on ${law.name}`}</span></Button>
          : <Button id={`law-open-${law.key}`} variant={law === firstWaiting && !stale ? 'primary' : 'secondary'} onClick={() => open(law)}>{`Decide on ${law.name}`}</Button>;
    }
    const eff = law.effective;
    const soon = eff && isFuture(eff, today) ? ` · in ${daysUntil(eff, today)} days` : '';
    return (
      <li key={law.key} className="cd-law" id={`law-${law.key}`} data-state={law.current ? 'decided' : 'needed'} data-open={isOpen ? 'true' : 'false'} data-law={law.key}>
        <div className="cd-law-top">
          <div className="cd-law-id">
            <h4 className="cd-law-name" id={`law-name-${law.key}`} tabIndex={-1}>{law.name}</h4>
            <p className="cd-law-status">
              <span className="cd-stay">{Glyph[law.stayIcon]}{law.stayLabel}</span>
              {!isOpen && eff ? <><span className="cd-sep" aria-hidden="true">·</span><span>{`${effWord(eff, today)} ${fmtLawDate(eff)}`}</span></> : null}
            </p>
          </div>
          <Chip law={law} />
        </div>
        {f ? (
          <Alert variant="success" live={false} part="flash">
            <p>{f.text}</p>
            {keptHere ? <>
              <div className="cd-actions"><Button onClick={() => void copy(law.key, kept!.draft.reason, 'reason')}>Copy my reason</Button></div>
              {copyOut(law.key)}
            </> : null}
          </Alert>
        ) : null}
        {!law.current ? (
          <p className="cd-line">{canDecide
            ? <><b>Not counted</b> in obligations, readiness or your pack until an owner or admin decides.</>
            : <><b>Not counted</b>{` until ${names.length ? orList(names) : 'an owner or admin'} decides.`}</>}</p>
        ) : null}
        {!isOpen ? (law.current
          ? <DecisionRecordBox who={recordWho(law.current)} reason={law.current.reason} />
          : (
            <div className="cd-systems">
              <span>{`${law.systems.length} of your systems:`}</span>
              <ul>{law.systems.map((s) => <li key={s.system_id}><Link to={`/w/${encodeURIComponent(slug)}/app/systems/${encodeURIComponent(s.system_id)}`}>{s.name}</Link></li>)}</ul>
            </div>
          )) : null}
        {isOpen ? (
          <div className={`cd-panel${ro ? ' cd-ro' : ''}`}>
            <div className="cd-col-facts">
              <p className="cd-plain">{law.plain}</p>
              <dl className="cd-facts">
                {eff ? <div><dt>{effWord(eff, today)}</dt><dd>{fmtLawDate(eff)}<span className="cd-soon">{soon}</span></dd></div> : null}
                <div><dt>Your systems</dt><dd><ul>{law.systems.map((s) => <li key={s.system_id}><Link to={`/w/${encodeURIComponent(slug)}/app/systems/${encodeURIComponent(s.system_id)}`}>{s.name}</Link></li>)}</ul></dd></div>
              </dl>
              {law.current && !ro ? <DecisionRecordBox who={recordWho(law.current)} reason={law.current.reason} /> : null}
              <History law={law} />
              <Source law={law} />
            </div>
            <div className="cd-col-form">{ro ? whoPanel(law) : decideForm(law)}</div>
          </div>
        ) : (
          <div className="cd-foot">
            <div className="cd-actions">{action}</div>
            <div className="cd-meta">{law.current ? <History law={law} /> : null}<Source law={law} /></div>
          </div>
        )}
      </li>
    );
  }

  const anyOpen = !!openKey;
  return (
    <section className="cd-view" aria-label="Decisions" data-part="decisions">
      <div ref={liveRef} className="sr-only" role="status" aria-live="polite" />
      {from ? <p className="cd-ret"><Link to={from}>{Glyph.back}{`Back to ${fromName}`}</Link></p> : null}
      {stale ? (
        <Alert variant="info" title="This law no longer needs a decision for your systems." focusTitle={{ id: 'cd-stale-title' }} part="stale">
          <div className="cd-actions">
            {from ? backLink(true) : null}
            <Link className={`gp-linkbtn${from ? '' : ' btn-primary'}`} to={overviewHref}>See how it&apos;s counted</Link>
          </div>
        </Alert>
      ) : null}
      {laws.length === 0 ? (
        <EmptyState title="No paused or contested law affects your systems." description="Every law that applies to your systems is counted as normal." action={backLink(true)} />
      ) : <>
        {!waiting.length && canDecide && !anyOpen ? (
          <div className="cd-done"><p className="cd-line"><b>Nothing waiting.</b> Every law on hold has a decision.</p>{backLink(true)}</div>
        ) : null}
        {waiting.length ? <>
          <h3 className="cd-group">Waiting on a decision <span className="cd-n">{`· ${waiting.length}`}</span></h3>
          <ul className="cd-laws" aria-label="Waiting on a decision">{waiting.map((l) => card(l, waiting[0]))}</ul>
        </> : null}
        {decided.length ? <>
          <h3 className="cd-group">Decided <span className="cd-n">{`· ${decided.length}`}</span></h3>
          <ul className="cd-laws" aria-label="Decided">{decided.map((l) => card(l, waiting[0]))}</ul>
        </> : null}
      </>}
    </section>
  );
}

/** The Scope tab's way in: one line and "Review <law>" / "Review <n> decisions" while laws wait on a decision. */
export function DecisionsEntry({ slug }: { slug: string }) {
  const q = useRuleDecisions(slug);
  const waiting = useMemo(() => splitLaws(groupByLaw(q.data?.decisions ?? [])).waiting, [q.data]);
  if (!waiting.length) return null;
  const n = waiting.length;
  const t = reviewTarget(slug, waiting);
  return (
    <div className="cd-entry" data-part="decisions-entry">
      <p><b>{n === 1 ? '1 law on hold needs a decision.' : `${n} laws on hold need a decision.`}</b>{n === 1 ? ' Until then it counts toward nothing.' : ' Until then they count toward nothing.'}</p>
      <Link className="gp-linkbtn btn-primary" to={t.href}>{t.label}</Link>
    </div>
  );
}

/** Today, owners and admins only: the law(s) waiting on a decision, with the urgency in the title. */
export function DecisionsTodayItem({ slug }: { slug: string }) {
  const q = useRuleDecisions(slug);
  const waiting = useMemo(() => splitLaws(groupByLaw(q.data?.decisions ?? [])).waiting, [q.data]);
  if (!q.data?.can_decide || !waiting.length) return null;
  const t = reviewTarget(slug, waiting);
  const today = todayIso();
  return (
    <section className="gp-sec" aria-labelledby="decisions-today-h" data-part="decisions-today">
      <div className="gp-sec-head"><h2 id="decisions-today-h" className="gp-sec-h">Decisions</h2></div>
      <div className="cd-titem">
        <div className="cd-titem-body">
          <p className="cd-titem-t">{todayTitle(waiting, today)}</p>
          <p className="cd-titem-s">{todaySub(waiting)}</p>
        </div>
        <Link className="gp-linkbtn" to={t.href}>{t.label}</Link>
      </div>
    </section>
  );
}
